import { Heading } from "@ariakit/react";
import {
  Failure,
  fold,
  idToDate,
  MiddotSeparated,
  Success,
  swrResponseToResult,
  type FailurePayload,
} from "@indietabletop/appkit";
import useSWR, { type SWRConfiguration } from "swr";
import { Link, Route, Switch, useParams } from "wouter";
import { FailureView } from "../failures/FailureView.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import { Page } from "../Page/Page.tsx";
import { section } from "../SettingsPage/style.css.ts";
import { RichText } from "./RichText.tsx";
import { doc, item, page } from "./style.css.ts";
import type { ChangelogItem, HelpItem, WithContent } from "./types.ts";

const shortDate = Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

async function fromCallback<T>(
  callback: () => T | Promise<T>
): Promise<Success<T> | Failure<unknown>> {
  try {
    return new Success(await callback());
  } catch (error) {
    return new Failure(error);
  }
}

export type DocContentTypeMismatch = {
  type: "DOC_CONTENT_TYPE_MISMATCH";
};

type FetchJsonResult<T> =
  | Success<T>
  | Failure<FailurePayload | DocContentTypeMismatch>;

export async function fetchJson<T>(url: string): Promise<FetchJsonResult<T>> {
  const result = await fromCallback(() => fetch(url));

  if (result.isFailure) {
    return new Failure({ type: "NETWORK_ERROR" });
  }

  const { value: response } = result;
  if (!response.ok) {
    return new Failure({ type: "API_ERROR", code: response.status });
  }

  // This can happen due to Netlify rewrites serving index.html in case the
  // requested doc file is not found.
  if (response.headers.get("Content-Type") !== "application/json") {
    return new Failure({ type: "DOC_CONTENT_TYPE_MISMATCH" });
  }

  const parsed = await fromCallback(() => response.json());
  if (parsed.isFailure) {
    return new Failure({ type: "UNKNOWN_ERROR" });
  }

  return parsed;
}

export function useJsonFetcher<T = unknown>(
  url: string,
  config?: SWRConfiguration
) {
  return useSWR<FetchJsonResult<T>>(url, fetchJson, config);
}

export function DocPages() {
  return (
    <Switch>
      <Route path="/">
        <DocPagesIndex />
      </Route>

      <Route path="/:dir/:id">
        <HelpPage />
      </Route>
    </Switch>
  );
}

function HelpPage() {
  const { id, dir } = useParams<{ id: string; dir: string }>();
  const result = swrResponseToResult(
    useJsonFetcher<WithContent<ChangelogItem | HelpItem>>(
      `/docs/${dir}/${id}.json`
    )
  );

  if (result.isPending) {
    return (
      <Page title="Docs" backHref="/" centerChildren>
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page title="Docs" backHref="/" centerChildren>
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  const payload = result.value;
  return (
    <Page title="Docs" backHref="/">
      <div className={doc.paper}>
        <header className={doc.header}>
          <Heading className={doc.title}>{payload.data.title}</Heading>

          <MiddotSeparated className={doc.subheading}>
            {dir === "changelog" &&
              shortDate.format(idToDate(id, () => new Date("2025-01-01")))}

            {payload.data.author}
          </MiddotSeparated>
        </header>

        <RichText trustedHtml={payload.content} />
      </div>
    </Page>
  );
}

function DocPagesIndexSuccess(props: {
  changelog: ChangelogItem[];
  help: HelpItem[];
}) {
  const { help, changelog } = props;

  return (
    <div className={doc.paper}>
      <div className={page.section}>
        <Heading className={section.heading}>Changelog</Heading>

        <ul className={page.items}>
          {changelog.map((doc) => {
            const publishedDate = idToDate(
              doc.id,
              () => new Date("2025-01-01")
            );

            return (
              <li key={doc.id} className={item.container}>
                <Link href={`/changelog/${doc.id}`}>
                  <div className={item.title}>{doc.data.title}</div>
                  <div className={item.subtitle}>
                    {shortDate.format(publishedDate)}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <div className={page.section}>
        <Heading className={section.heading}>Help</Heading>

        <ul className={page.items}>
          {help
            .map((doc) => {
              return (
                <li key={doc.id} className={item.container}>
                  <Link href={`/help/${doc.id}`}>
                    <div className={item.title}>{doc.data.title}</div>
                    <div className={item.subtitle}>{doc.data.standalone}</div>
                  </Link>
                </li>
              );
            })
            .reverse()}
        </ul>
      </div>
    </div>
  );
}

function DocPagesIndex() {
  const result = fold([
    swrResponseToResult(
      useJsonFetcher<ChangelogItem[]>("/docs/changelog/index.json")
    ),
    swrResponseToResult(useJsonFetcher<HelpItem[]>("/docs/help/index.json")),
  ]);

  if (result.isPending) {
    return (
      <Page title="Docs" backHref="~/" centerChildren>
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page title="Docs" backHref="~/" centerChildren>
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  const [changelog, help] = result.value;
  return (
    <Page title="Docs" backHref="~/">
      <DocPagesIndexSuccess changelog={changelog} help={help} />
    </Page>
  );
}
